'use client';

import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useIsMarketingRoute } from '../lib/use-marketing-route';
import { authService } from '../lib/auth.service';
import { supabase } from '../lib/supabase';
import { loginRedirectPath } from '../mobile/lib/capacitor-bridge';
import type { AppUser } from '../lib/users.service';
import {
  canViewSection,
  canEditSection,
  getEffectiveLevel,
  mergePermissions,
  type PermissionValue,
  type AccessLevel,
} from '../lib/permissions';
import { installFetchAuth, setFetchAuthUserId } from '../lib/fetch-auth';

// Recruiting is split into two provider-type modules. The legacy `recruiting`
// platform string is still used as an aggregate for routing and sidebar
// access — it is synthesized from these two whenever the user has either.
const RECRUITING_MODULES = ['recruiting-md', 'recruiting-crna'] as const;
type RecruitingModule = typeof RECRUITING_MODULES[number];
const RECRUITING_MODULE_TO_PROVIDER_TYPE: Record<RecruitingModule, 'MD' | 'CRNA'> = {
  'recruiting-md': 'MD',
  'recruiting-crna': 'CRNA',
};

const ALL_PLATFORMS = ['dashboard', 'roster', 'financial', 'advanced-analytics', 'business-development', 'recruiting', 'recruiting-md', 'recruiting-crna', 'credentialing', 'onboarding', 'scheduling', 'daily-scheduler', 'locums', 'tutorials', 'tools', 'report-issue', 'policies', 'compensation', 'surge-coverage', 'admin-tutorials', 'facility-dashboards', 'staffing-agencies', 'communications', 'documents', 'case-records', 'admin-communications', 'campaign'];

// Platforms restricted to developer and super_admin roles only
const DEVELOPER_ONLY_PLATFORMS = ['case-analytics', 'email-client'];

// Platforms auto-granted to developer + super_admin regardless of
// user_platform_roles state. Not grantable to any other role.
const INTELLIGENCE_AUTO_PLATFORMS = ['intelligence'];

// Platforms that providers can potentially access via role assignment
// Financial and admin are excluded — financial requires explicit admin grant, admin is super-admin only
const PROVIDER_PLATFORMS = [
  'daily-scheduler', 'scheduling', 'surge-coverage', 'tutorials', 'report-issue',
  'policies', 'tools', 'compensation', 'recruiting', 'recruiting-md', 'recruiting-crna',
  'credentialing', 'onboarding', 'locums', 'roster', 'provider', 'rates',
  // Provider Communications portal — chiefs (Department Chair role) need
  // it visible in their sidebar even though they're provider-linked users.
  'communications',
];

// ─── Section key mapping ─────────────────────────────────────────────────────
// Maps sidebar menu item labels to section keys used in role permissions JSONB
// This is the single source of truth linking the sidebar to the RBAC system

const MENU_LABEL_TO_SECTION_KEY: Record<string, Record<string, string>> = {
  roster: {
    'Dashboard': 'dashboard',
    'Providers': 'providers',
    'Facilities': 'facilities',
    'Surgeons': 'surgeons',
    'Provider Directory': 'directory',
    'Shifts': 'shifts',
    'Settings': 'settings',
  },
  financial: {
    'Financial Overview': 'financial_overview',
    'Daily Costs': 'daily_costs',
    'Provider Earnings': 'provider_earnings',
    'Revenue': 'revenue',
    'Expenses': 'expenses',
    'Shifts': 'shifts',
    'Payroll': 'payroll',
    'Reports': 'reports',
    'Surge Coverage': 'surge_coverage',
    'Import': 'import',
    'Settings': 'settings',
  },
  recruiting: {
    'Dashboard': 'dashboard',
    'All Recruits': 'recruits',
    'Calendar': 'calendar',
    'Settings': 'settings',
  },
  scheduling: {
    'Admin Hub': 'settings',
    'Dashboard': 'dashboard',
    'Schedule Builder': 'builder',
    'Monthly Calendar': 'calendar',
    'Provider Schedule': 'provider_schedule',
    'Shifts': 'shifts',
    'Providers': 'providers',
    'Work Patterns': 'work_patterns',
    // Tier-based sections (view = request/view, edit = manage/approve, admin = full)
    'Vacation': 'vacation',
    'Available Shifts': 'available_shifts',
    'Time Tracking': 'time_tracking',
    'Compensation Engine (Admin)': 'compensation',
    'Surge Coverage': 'surge_coverage',
    'Settings': 'settings',
  },
  rates: {},
  'advanced-analytics': {
    // Tabs on the Advanced Analytics platform — empty permissions = full access.
    // Future: gate individual tabs (Workforce / Margin = sensitive) per-role.
    'Collections': 'collections',
    'Case Mix': 'case_mix',
    'Workforce': 'workforce',
    'Margin': 'margin',
  },
  'business-development': {
    // Sub-pages on the Business Development platform. Empty permissions = full
    // access; per-section gating (e.g. Collections Estimator only) is a one-line
    // edit on the role row's permissions JSONB.
    'Hospital Hub': 'hospitals',
    'Pitch Pages': 'pitch_pages',
    'Coverage Model': 'coverage_model',
    'Collections Estimator': 'collections_estimator',
  },
  intelligence: {
    // Advanced Intelligence — developer + super_admin only platform
    // (2026-05-11). Auto-granted via INTELLIGENCE_AUTO_PLATFORMS below;
    // not exposed to user_platform_roles for other accounts.
    'Overview': 'overview',
    'Automations': 'automations',
    'Renegotiation': 'renegotiation',
    'IDR & Disputes': 'idr_disputes',
    'Strategy': 'strategy',
  },
  credentialing: {
    'Dashboard': 'dashboard',
    'Providers': 'providers',
    'Requirements': 'requirements',
    'Expirations': 'expirations',
    'Reports': 'reports',
    'Upcoming Providers': 'upcoming_providers',
  },
  onboarding: {
    'Dashboard': 'dashboard',
    'Providers': 'providers',
    'Tutorials': 'tutorials',
    'Checklists': 'checklist',
    'Settings': 'settings',
  },
  'daily-scheduler': {
    'Inbox': 'dashboard',
    'Dashboard': 'dashboard',
    'Make Schedule': 'schedule',
    'View Schedule': 'dashboard',
    'History': 'history',
    'Reports': 'reports',
    'Settings': 'settings',
  },
  locums: {
    'Dashboard': 'dashboard',
    'Client Outreach': 'outreach',
    'Providers': 'providers',
    'Facilities': 'facilities',
    'Job Postings': 'jobs',
    'Assignments': 'assignments',
    'Timesheets': 'timesheets',
    'Contracts': 'contracts',
    'Invoices': 'invoices',
    'Settings': 'settings',
  },
  tutorials: {
    'Dashboard': 'dashboard',
    'Browse Modules': 'browse',
    'My Progress': 'progress',
    'Settings': 'settings',
  },
  tools: {
    'Tools Dashboard': 'dashboard',
  },
  communications: {
    // Provider Communications portal — each tab is its own permission slot.
    'New Communication': 'compose',
    'Compose': 'compose',
    'Shift Posting': 'shift_posting',
    'Drafts': 'drafts',
    'Templates': 'templates',
    'History': 'history',
    'Providers': 'providers',
    'Distribution Lists': 'lists',
    'Signatures': 'signatures',
  },
  policies: {
    'Dashboard': 'dashboard',
    // Dynamic facility submenus are handled separately in Sidebar
    'Settings': 'settings',
  },
  'report-issue': {
    'Submit Report': 'submit',
    'My Reports': 'my_reports',
    'Admin': 'admin',
  },
  compensation: {
    'My Compensation': 'my',
    'Admin Dashboard': 'admin',
  },
  'surge-coverage': {
    'Request Coverage': 'request',
    'View Status': 'status',
  },
  'admin-tutorials': {
    'All Tutorials': 'dashboard',
  },
  tasks: {
    'Dashboard': 'dashboard',
    'My Tasks': 'my_tasks',
    'All Tasks': 'all_tasks',
    'Groups': 'groups',
  },
  'facility-dashboards': {
    'Hub': 'hub',
    'Facility Dashboard': 'facility_dashboard',
    'Admin Dashboard': 'admin_dashboard',
    'Settings': 'settings',
  },
  'admin-communications': {
    'Dashboard': 'dashboard',
  },
};

// ─── Types ───────────────────────────────────────────────────────────────────

interface PlatformRoleInfo {
  roleId: string;
  roleName: string;
  permissions: Record<string, PermissionValue>;
  /**
   * RBAC redesign signal (added 2026-05-11): a role-level admin flag from
   * `platform_roles.is_platform_admin`. Source of truth for "is this role
   * admin-tier", replacing the inference-from-permissions pattern
   * (`isAdminForPlatform(permissions)`).
   *
   * Defaults to `false` when missing — legacy code that still infers from
   * permissions continues to work via the existing utility. New code should
   * prefer this flag.
   */
  isPlatformAdmin: boolean;
}

/**
 * Backwards-compat shim: if the user has `recruiting-md` or `recruiting-crna`
 * (the two real modules), synthesize a `recruiting` aggregate entry in-place
 * so that every existing `hasAccess('recruiting')` / `canView('recruiting',
 * ...)` call keeps working. The aggregate's permissions are the max tier per
 * section across the two real modules.
 *
 * Safe to call with either the base-role map or the override map.
 */
function synthesizeRecruitingAggregate(
  map: Record<string, PlatformRoleInfo>
): void {
  const md = map['recruiting-md'];
  const crna = map['recruiting-crna'];
  if (!md && !crna) return;
  // Prefer admin-tier module as source of role identity (arbitrary — both
  // real modules typically share the same role_id anyway).
  const primary = md ?? crna;
  if (!primary) return;
  map['recruiting'] = {
    roleId: primary.roleId,
    roleName: primary.roleName,
    permissions: mergePermissions(
      [md?.permissions, crna?.permissions].filter(
        (p): p is Record<string, PermissionValue> => Boolean(p)
      )
    ),
    // Aggregate inherits admin-ness if EITHER source role is admin-tier.
    isPlatformAdmin: (md?.isPlatformAdmin === true) || (crna?.isPlatformAdmin === true),
  };
}

// Provider-specific access settings
interface ProviderAccessInfo {
  has_daily_scheduler_access: boolean;
  has_scheduling_access: boolean;
  has_surge_coverage_access: boolean;
  can_view_schedule: boolean;
  can_submit_addons: boolean;
  can_request_time_off: boolean;
  can_view_payroll: boolean;
  can_view_colleagues: boolean;
}

// View mode for profile switching
export type ViewMode = 'developer' | 'super_admin' | 'admin' | 'provider' | 'facility';

// Facility module key → platform slug mapping for route access
const FACILITY_MODULE_PLATFORMS: Record<string, string[]> = {
  provider_directory: ['roster'],
  daily_schedule: ['daily-scheduler'],
  schedule_upload: ['daily-scheduler'],
  surge_coverage: ['financial'],
  submit_report: ['report-issue'],
  reporting_link: [],
};

// Facility user info from resolve-user
interface FacilityUserInfo {
  id: string | null;
  modules: string[];
  is_active: boolean;
  facility_role_id: string | null;
}

interface FacilityInfo {
  id: string;
  name: string;
  short_name: string;
}

interface FacilityRoleInfo {
  id: string;
  name: string;
  greeting_template: string;
  greeting_style: string;
  modules: string[];
}

// Facility assignment from user_facilities table
interface FacilityAssignment {
  facility_id: string | null; // null = Division (all facilities)
  facilities: { id: string; name: string; short_name: string } | null;
}

interface AuthContextType {
  user: AppUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<AppUser>;
  logout: () => Promise<void>;
  isDeveloper: boolean;
  isSuperAdmin: boolean;
  isAdmin: boolean;
  isReadOnly: boolean;
  isProvider: boolean;
  /** System role names assigned to this user (e.g. ['Developer'],
   *  ['Department Chair'], ['Provider']). RBAC Round 4: replaces the
   *  legacy users.role string. */
  userSystemRoles: string[];
  isEquityPartner: boolean;
  canEdit: boolean;
  accessiblePlatforms: string[];
  hasAccess: (platform: string) => boolean;
  userPlatformRoles: Record<string, PlatformRoleInfo>;
  hasSectionAccess: (platform: string, sectionKey: string) => boolean;
  getAccessLevel: (platform: string, sectionKey: string) => AccessLevel;
  getUserPlatforms: () => string[];
  refreshPlatformRoles: () => Promise<void>;
  providerAccess: ProviderAccessInfo | null;
  userType: 'admin' | 'provider' | 'facility' | null;
  // Facility user
  isFacilityUser: boolean;
  facilityUser: FacilityUserInfo | null;
  facilityInfo: FacilityInfo | null;
  facilityRole: FacilityRoleInfo | null;
  // Profile switching
  viewMode: ViewMode | null;
  setViewMode: (mode: ViewMode) => void;
  availableViewModes: ViewMode[];
  linkedProviderId: string | null;
  // Phase 2: Enhanced permission methods
  canView: (platform: string, sectionKey: string) => boolean;
  canEditSection: (platform: string, sectionKey: string) => boolean;
  isAdminForPlatform: (platform: string) => boolean;
  platformOverrides: Record<string, PlatformRoleInfo>;
  loadOverrides: () => Promise<void>;
  // Facility assignments
  facilityAssignments: FacilityAssignment[];
  hasAllFacilityAccess: boolean; // true if user has Division assignment (facility_id = null)
  assignedFacilityIds: string[]; // list of specific facility UUIDs the user can access
  // Recruiting provider-type modules the user has access to.
  // - ['MD','CRNA'] = unrestricted (sees both)
  // - ['CRNA']       = CRNA/SRNA recruits only
  // - ['MD']         = MD recruits only
  // - []             = no recruiting access (recruiting sidebar entry hidden)
  recruitingProviderModules: Array<'MD' | 'CRNA'>;
  // System-role capabilities — fine-grained permissions for UI actions that
  // don't fit the platform/module/v-e-a model (e.g. granting Super Admin to
  // another user). Backed by `system_role_capabilities`. See /api/user-capabilities.
  userCapabilities: string[];
  hasCapability: (capability: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const STORAGE_KEY = 'meridian_financial_auth_user';
// v2 — bumped 2026-04-28 when 'communications' was promoted to a top-level
// platform. Old cached role maps don't include it, so the sidebar wouldn't
// show Provider Communications until the user re-logged in. Bumping the key
// forces every existing session to refetch on next page load.
const ROLES_STORAGE_KEY = 'meridian_platform_roles_v2';
// System roles (e.g. ['Developer'] / ['Super Admin']) cached for instant
// render — added 2026-06-04. Every other identity signal (user, platform
// roles, userType, facility) was already cached; system roles alone were
// re-fetched from /api/user-capabilities on EVERY load with no seed. When
// that fetch was slow/empty at the redirect moment, a Developer/Super Admin's
// role flags all collapsed to false and app/page.tsx stranded them on
// /tutorials. Seeding from localStorage closes that race for returning users.
const SYSTEM_ROLES_STORAGE_KEY = 'meridian_system_roles';
const USER_TYPE_KEY = 'meridian_user_type';
const PROVIDER_ACCESS_KEY = 'meridian_provider_access';
const VIEW_MODE_KEY = 'meridian_view_mode';
const OVERRIDES_STORAGE_KEY = 'meridian_platform_overrides';
const FACILITY_ASSIGNMENTS_KEY = 'meridian_facility_assignments';
const FACILITY_USER_KEY = 'meridian_facility_user';
const FACILITY_INFO_KEY = 'meridian_facility_info';
const FACILITY_ROLE_KEY = 'meridian_facility_role';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Public marketing tree (/website/*) renders auth-inert: no localStorage
  // restore, no session checks, no role/capability fetches, no auth
  // listeners, no fetch-auth interceptor. The marketing origin must not talk
  // to Supabase or platform APIs at all. See app/lib/marketing-route.ts.
  // Segment-aware: on the apex the address bar reads "/about", not "/website/about".
  const marketingInert = useIsMarketingRoute();

  // Install the global fetch interceptor exactly once. It attaches an
  // `Authorization: Bearer <session access_token>` header to every
  // same-origin /api/* request, so server gates (resolveAuthContext /
  // requireAdmin / …) authenticate the caller even when a page uses plain
  // fetch(). (The old spoofable `?callerAuthId=` URL-param injection was
  // removed 2026-06-21.) See app/lib/fetch-auth.ts.
  useEffect(() => {
    if (marketingInert) return;
    installFetchAuth();
  }, [marketingInert]);

  // Keep the interceptor's current auth user id in sync with this
  // provider's resolved user.
  useEffect(() => {
    setFetchAuthUserId(user?.auth_user_id ?? null);
  }, [user?.auth_user_id]);
  const [userPlatformRoles, setUserPlatformRoles] = useState<Record<string, PlatformRoleInfo>>({});
  const [userType, setUserType] = useState<'admin' | 'provider' | 'facility' | null>(null);
  const [providerAccess, setProviderAccess] = useState<ProviderAccessInfo | null>(null);
  const [viewMode, setViewModeState] = useState<ViewMode | null>(null);
  const [platformOverrides, setPlatformOverrides] = useState<Record<string, PlatformRoleInfo>>({});
  // Fine-grained system-role capabilities (e.g. "grant_super_admin",
  // "grant_financial_access"). Populated from /api/user-capabilities.
  const [userCapabilities, setUserCapabilities] = useState<string[]>([]);
  // Names of the system roles assigned to the user (e.g. ['Developer'] or
  // ['Super Admin', 'Chief CRNA']). Populated from /api/user-capabilities
  // alongside capabilities. Added 2026-05-11 for RBAC Slice B: AuthContext
  // derives `isDeveloper` / `isSuperAdmin` from this list (with legacy
  // `users.role` as fallback) instead of reading the legacy column directly.
  const [userSystemRoles, setUserSystemRoles] = useState<string[]>([]);
  // Store the user's own platform roles so we can restore them when switching back from provider view
  const [ownPlatformRoles, setOwnPlatformRoles] = useState<Record<string, PlatformRoleInfo>>({});
  const [providerSystemRoles, setProviderSystemRoles] = useState<Record<string, PlatformRoleInfo> | null>(null);
  const [facilityAssignments, setFacilityAssignments] = useState<FacilityAssignment[]>([]);
  const [facilityUser, setFacilityUser] = useState<FacilityUserInfo | null>(null);
  const [facilityInfo, setFacilityInfo] = useState<FacilityInfo | null>(null);
  const [facilityRole, setFacilityRole] = useState<FacilityRoleInfo | null>(null);

  interface ResolveResult {
    user: AppUser | null;
    userType: 'admin' | 'provider' | 'facility' | null;
    providerAccess: ProviderAccessInfo | null;
    facilityAssignments: FacilityAssignment[];
    facilityUser: FacilityUserInfo | null;
    facilityInfo: FacilityInfo | null;
    facilityRole: FacilityRoleInfo | null;
  }

  const resolveDbUser = useCallback(async (authUserId: string, email: string | undefined): Promise<ResolveResult> => {
    const empty: ResolveResult = { user: null, userType: null, providerAccess: null, facilityAssignments: [], facilityUser: null, facilityInfo: null, facilityRole: null };
    try {
      const res = await fetch('/api/resolve-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ authUserId, email }),
      });

      if (!res.ok) {
        // 5xx = transient backend failure (e.g. a poisoned-lambda window) —
        // signal it so checkSession preserves the cached session instead of
        // logging the user out on a blip. 4xx = authoritative (bad/expired auth,
        // bad request) → return empty so the caller clears.
        if (res.status >= 500) {
          const e = new Error(`resolve-user ${res.status}`);
          e.name = 'TransientAuthError';
          throw e;
        }
        return empty;
      }

      const data = await res.json();
      const resolvedUser = data.user && data.user.is_active ? data.user : null;
      const resolvedUserType = data.userType || null;
      const resolvedProviderAccess = data.user?.provider_access || null;
      const resolvedFacilityAssignments = data.facilityAssignments || [];

      return {
        user: resolvedUser,
        userType: resolvedUserType,
        providerAccess: resolvedProviderAccess,
        facilityAssignments: resolvedFacilityAssignments,
        facilityUser: data.facilityUser || null,
        facilityInfo: data.facility || null,
        facilityRole: data.facilityRole || null,
      };
    } catch (err) {
      // Re-throw an explicit transient signal; treat any other fetch throw
      // (network drop, aborted/timed-out request) as transient too — never a
      // confident "this user is logged out".
      if (err instanceof Error && err.name === 'TransientAuthError') throw err;
      const e = new Error('resolve-user network failure');
      e.name = 'TransientAuthError';
      throw e;
    }
  }, []);

  // Load platform roles for the current user
  const loadPlatformRoles = useCallback(async (appUser: AppUser) => {

    try {
      const res = await fetch(`/api/admin/user-roles?userId=${appUser.id}&callerAuthId=${appUser.auth_user_id}`);
      if (!res.ok) {
        // Transient failure (5xx, network blip, or a timed-out user-roles
        // lambda — see the 2026-06-06 chair-lockout incident, where this
        // endpoint intermittently 500'd with "No response is returned from
        // the function"). Do NOT clobber roles to {}: that demotes a dual-role
        // admin/chair to "no effective access" and, via app/page.tsx's
        // fallback routing, traps them in a / ↔ /provider redirect loop
        // ("refreshing over and over, then denied access"). Preserve the
        // last-known-good roles (seeded from localStorage on mount) and let
        // the next successful fetch reconcile. Mirrors the loadCapabilities
        // preserve-on-failure fix from 2026-06-04 (63f4672c), which was never
        // applied to this sibling function.
        const body = await res.text().catch(() => '<unreadable>');
        console.warn('[AuthContext] user-roles fetch failed', res.status, body, '— preserving cached roles');
        return;
      }

      const { userRoles } = await res.json();
      const rolesMap: Record<string, PlatformRoleInfo> = {};

      if (Array.isArray(userRoles)) {
        for (const ur of userRoles) {
          if (ur.platform_roles && ur.platform) {
            rolesMap[ur.platform] = {
              roleId: ur.platform_roles.id,
              roleName: ur.platform_roles.name,
              permissions: ur.platform_roles.permissions || {},
              isPlatformAdmin: ur.platform_roles.is_platform_admin === true,
            };
          }
        }
      }

      synthesizeRecruitingAggregate(rolesMap);
      setUserPlatformRoles(rolesMap);
      localStorage.setItem(ROLES_STORAGE_KEY, JSON.stringify(rolesMap));
    } catch (err) {
      // Same rationale as the !res.ok branch — a thrown fetch (network drop,
      // aborted/timed-out request) is transient. Preserve cached roles rather
      // than demoting the user into the redirect loop.
      console.warn('[AuthContext] user-roles fetch threw — preserving cached roles', err);
    }
  }, []);

  // Load platform overrides for admin users
  const loadOverrides = useCallback(async (appUser?: AppUser | null) => {
    const targetUser = appUser || user;
    if (!targetUser) {
      setPlatformOverrides({});
      localStorage.removeItem(OVERRIDES_STORAGE_KEY);
      return;
    }

    try {
      const res = await fetch(`/api/admin/overrides?userId=${targetUser.id}&callerAuthId=${targetUser.auth_user_id}`);
      if (!res.ok) {
        setPlatformOverrides({});
        return;
      }

      const { overrides } = await res.json();
      const overridesMap: Record<string, PlatformRoleInfo> = {};

      if (Array.isArray(overrides)) {
        for (const ov of overrides) {
          if (ov.platform_roles && ov.platform) {
            overridesMap[ov.platform] = {
              roleId: ov.platform_roles.id,
              roleName: ov.platform_roles.name,
              permissions: ov.platform_roles.permissions || {},
              isPlatformAdmin: ov.platform_roles.is_platform_admin === true,
            };
          }
        }
      }

      synthesizeRecruitingAggregate(overridesMap);
      setPlatformOverrides(overridesMap);
      localStorage.setItem(OVERRIDES_STORAGE_KEY, JSON.stringify(overridesMap));
    } catch {
      setPlatformOverrides({});
    }
  }, [user]);

  // Load fine-grained system-role capabilities for the signed-in user.
  // Safe to call for any user — returns an empty list if the user has no
  // system roles assigned or no capabilities attached to those roles.
  const loadCapabilities = useCallback(async (appUser?: AppUser | null) => {
    const targetUser = appUser || user;
    if (!targetUser?.auth_user_id) {
      // No authenticated user — this is a genuine "logged out" state, so it's
      // safe to clear the cached roles.
      setUserCapabilities([]);
      setUserSystemRoles([]);
      localStorage.removeItem(SYSTEM_ROLES_STORAGE_KEY);
      return;
    }
    try {
      const res = await fetch(`/api/user-capabilities?callerAuthId=${targetUser.auth_user_id}`);
      if (!res.ok) {
        // Transient failure (5xx, network blip, a momentarily-degraded lambda).
        // Do NOT clobber the user's roles to [] — that's exactly what demotes a
        // Developer/Super Admin to "unknown role" and strands them on /tutorials.
        // Keep the last-known-good roles (seeded from localStorage on mount) and
        // let the next successful fetch reconcile.
        console.warn('[AuthContext] user-capabilities fetch failed', res.status, '— preserving cached roles');
        return;
      }
      const { capabilities, systemRoles } = await res.json();
      // Only treat the response as authoritative when it actually carries a
      // systemRoles array. (The route omits the field on its no-user branch.)
      if (Array.isArray(systemRoles)) {
        setUserSystemRoles(systemRoles);
        localStorage.setItem(SYSTEM_ROLES_STORAGE_KEY, JSON.stringify(systemRoles));
      }
      if (Array.isArray(capabilities)) {
        setUserCapabilities(capabilities);
      }
    } catch {
      // Same rationale as the !res.ok branch — a thrown fetch is transient;
      // preserve cached roles rather than demoting the user.
      console.warn('[AuthContext] user-capabilities fetch threw — preserving cached roles');
    }
  }, [user]);

  const refreshPlatformRoles = useCallback(async () => {
    if (user) {
      await loadPlatformRoles(user);
      await loadOverrides(user);
      await loadCapabilities(user);
    }
  }, [user, loadPlatformRoles, loadOverrides, loadCapabilities]);

  useEffect(() => {
    // Marketing pages: skip restore, session checks, and auth listeners.
    if (marketingInert) {
      setLoading(false);
      return;
    }
    // Restore from localStorage for instant render
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        setUser(JSON.parse(stored));
      }
      const storedRoles = localStorage.getItem(ROLES_STORAGE_KEY);
      if (storedRoles) {
        setUserPlatformRoles(JSON.parse(storedRoles));
      }
      // Seed system roles for instant render so isDeveloper/isSuperAdmin resolve
      // on the first paint instead of racing the /api/user-capabilities fetch.
      const storedSystemRoles = localStorage.getItem(SYSTEM_ROLES_STORAGE_KEY);
      if (storedSystemRoles) {
        setUserSystemRoles(JSON.parse(storedSystemRoles));
      }
      const storedUserType = localStorage.getItem(USER_TYPE_KEY);
      if (storedUserType) {
        setUserType(storedUserType as 'admin' | 'provider');
      }
      const storedProviderAccess = localStorage.getItem(PROVIDER_ACCESS_KEY);
      if (storedProviderAccess) {
        setProviderAccess(JSON.parse(storedProviderAccess));
      }
      const storedOverrides = localStorage.getItem(OVERRIDES_STORAGE_KEY);
      if (storedOverrides) {
        setPlatformOverrides(JSON.parse(storedOverrides));
      }
      const storedFacilityAssignments = localStorage.getItem(FACILITY_ASSIGNMENTS_KEY);
      if (storedFacilityAssignments) {
        setFacilityAssignments(JSON.parse(storedFacilityAssignments));
      }
      const storedFacilityUser = localStorage.getItem(FACILITY_USER_KEY);
      if (storedFacilityUser) {
        setFacilityUser(JSON.parse(storedFacilityUser));
      }
      const storedFacilityInfo = localStorage.getItem(FACILITY_INFO_KEY);
      if (storedFacilityInfo) {
        setFacilityInfo(JSON.parse(storedFacilityInfo));
      }
      const storedFacilityRole = localStorage.getItem(FACILITY_ROLE_KEY);
      if (storedFacilityRole) {
        setFacilityRole(JSON.parse(storedFacilityRole));
      }
    } catch { /* ignore */ }

    // Check actual session
    async function checkSession() {
      try {
        const session = await authService.getSession();
        if (!session) {
          setUser(null);
          setUserType(null);
          setProviderAccess(null);
          setPlatformOverrides({});
          setFacilityAssignments([]);
          setFacilityUser(null);
          setFacilityInfo(null);
          setFacilityRole(null);
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(ROLES_STORAGE_KEY);
          localStorage.removeItem(USER_TYPE_KEY);
          localStorage.removeItem(PROVIDER_ACCESS_KEY);
          localStorage.removeItem(OVERRIDES_STORAGE_KEY);
          localStorage.removeItem(FACILITY_ASSIGNMENTS_KEY);
          localStorage.removeItem(FACILITY_USER_KEY);
          localStorage.removeItem(FACILITY_INFO_KEY);
          localStorage.removeItem(FACILITY_ROLE_KEY);
          localStorage.removeItem(SYSTEM_ROLES_STORAGE_KEY);
          setLoading(false);
          return;
        }

        const result = await resolveDbUser(session.user.id, session.user.email);
        if (result.user) {
          setUser(result.user);
          setUserType(result.userType);
          setProviderAccess(result.providerAccess);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(result.user));
          if (result.userType) {
            localStorage.setItem(USER_TYPE_KEY, result.userType);
          }
          if (result.providerAccess) {
            localStorage.setItem(PROVIDER_ACCESS_KEY, JSON.stringify(result.providerAccess));
          }
          // Save facility assignments from resolve-user response
          if (result.facilityAssignments) {
            setFacilityAssignments(result.facilityAssignments);
            localStorage.setItem(FACILITY_ASSIGNMENTS_KEY, JSON.stringify(result.facilityAssignments));
          }
          // Save facility user data
          if (result.facilityUser) {
            setFacilityUser(result.facilityUser);
            localStorage.setItem(FACILITY_USER_KEY, JSON.stringify(result.facilityUser));
          }
          if (result.facilityInfo) {
            setFacilityInfo(result.facilityInfo);
            localStorage.setItem(FACILITY_INFO_KEY, JSON.stringify(result.facilityInfo));
          }
          if (result.facilityRole) {
            setFacilityRole(result.facilityRole);
            localStorage.setItem(FACILITY_ROLE_KEY, JSON.stringify(result.facilityRole));
          }
          // Load platform roles and overrides after user is resolved
          // Providers need roles loaded too for section-level permission checks (e.g. canEditSection)
          if (result.userType === 'admin' || result.userType === 'provider') {
            await loadPlatformRoles(result.user);
            await loadOverrides(result.user);
            await loadCapabilities(result.user);
          }
        } else {
          setUser(null);
          setUserType(null);
          setProviderAccess(null);
          setPlatformOverrides({});
          setFacilityAssignments([]);
          setFacilityUser(null);
          setFacilityInfo(null);
          setFacilityRole(null);
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(ROLES_STORAGE_KEY);
          localStorage.removeItem(USER_TYPE_KEY);
          localStorage.removeItem(PROVIDER_ACCESS_KEY);
          localStorage.removeItem(OVERRIDES_STORAGE_KEY);
          localStorage.removeItem(FACILITY_ASSIGNMENTS_KEY);
          localStorage.removeItem(FACILITY_USER_KEY);
          localStorage.removeItem(FACILITY_INFO_KEY);
          localStorage.removeItem(FACILITY_ROLE_KEY);
          localStorage.removeItem(SYSTEM_ROLES_STORAGE_KEY);
        }
      } catch (err) {
        // A thrown error here is transient: resolveDbUser now only throws on a
        // 5xx/aborted /api/resolve-user (e.g. a poisoned-lambda window) or
        // getSession itself failing. Do NOT log a returning user out on a
        // backend blip — that bounces them to /login and, for dual-role admins,
        // into the redirect loop. Preserve the cached identity (seeded from
        // localStorage on mount); the next successful checkSession or a
        // SIGNED_OUT event reconciles. Genuine sign-out flows through the
        // no-session branch above + the SIGNED_OUT listener, never this catch.
        // Fall back to clearing only when there's no cached user to preserve.
        let hasCachedUser = false;
        try { hasCachedUser = !!localStorage.getItem(STORAGE_KEY); } catch { /* ignore */ }
        if (hasCachedUser) {
          console.warn('[AuthContext] checkSession failed transiently — preserving cached session', err);
        } else {
          setUser(null);
          setUserType(null);
          setProviderAccess(null);
          setPlatformOverrides({});
          setFacilityAssignments([]);
          setFacilityUser(null);
          setFacilityInfo(null);
          setFacilityRole(null);
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(ROLES_STORAGE_KEY);
          localStorage.removeItem(USER_TYPE_KEY);
          localStorage.removeItem(PROVIDER_ACCESS_KEY);
          localStorage.removeItem(OVERRIDES_STORAGE_KEY);
          localStorage.removeItem(FACILITY_ASSIGNMENTS_KEY);
          localStorage.removeItem(FACILITY_USER_KEY);
          localStorage.removeItem(FACILITY_INFO_KEY);
          localStorage.removeItem(FACILITY_ROLE_KEY);
        }
      } finally {
        setLoading(false);
      }
    }

    checkSession();

    // Listen for auth state changes (including token refresh and sign-out)
    const { data: { subscription } } = authService.onAuthStateChange(
      (event: string) => {
        if (event === 'SIGNED_OUT') {
          setUser(null);
          setUserType(null);
          setProviderAccess(null);
          setUserPlatformRoles({});
          setUserSystemRoles([]);
          setPlatformOverrides({});
          setFacilityAssignments([]);
          setFacilityUser(null);
          setFacilityInfo(null);
          setFacilityRole(null);
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(ROLES_STORAGE_KEY);
          localStorage.removeItem(USER_TYPE_KEY);
          localStorage.removeItem(PROVIDER_ACCESS_KEY);
          localStorage.removeItem(OVERRIDES_STORAGE_KEY);
          localStorage.removeItem(FACILITY_ASSIGNMENTS_KEY);
          localStorage.removeItem(FACILITY_USER_KEY);
          localStorage.removeItem(FACILITY_INFO_KEY);
          localStorage.removeItem(FACILITY_ROLE_KEY);
          localStorage.removeItem(SYSTEM_ROLES_STORAGE_KEY);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [resolveDbUser, loadPlatformRoles, marketingInert]);

  // ── Session resilience: refresh session when app returns to foreground ──
  // This prevents the "frozen app" / "empty data" issue when the tab or
  // mobile app sits idle for hours and the JWT expires in the background.
  const lastVisibilityRefresh = useRef<number>(0);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (marketingInert) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;

      // Throttle: only refresh if >60 seconds since last refresh
      const now = Date.now();
      if (now - lastVisibilityRefresh.current < 60_000) return;
      lastVisibilityRefresh.current = now;

      try {
        // Nothing to refresh when nobody is signed in. auth-js reports a
        // missing session as AuthSessionMissingError with status 400 — the
        // same status as a definitive rejection below — so without this guard
        // every PUBLIC page (password-gated share views, token portals, offer
        // links) bounced its visitor to /login the first time the tab came
        // back into view. Found 2026-09-16 on /provider-tracker/[slug].
        const { data: current } = await supabase.auth.getSession();
        if (!current?.session) return;

        // Ask Supabase to refresh the session token
        const { data, error } = await supabase.auth.refreshSession();

        if (error || !data.session) {
          // Only a DEFINITIVE auth rejection (the server said the refresh token
          // is invalid/expired — HTTP 400/401/403) should force a re-login. A
          // transient failure (network blip, or the bounded fetch timeout in
          // lib/supabase.ts surfacing as an abort) must NOT log the user out:
          // bouncing to /login on a flaky connection is a worse regression than
          // the freeze we're fixing. Leave the session intact and let the
          // auto-refresh ticker retry on its own.
          const refreshStatus = (error as { status?: number } | null)?.status;
          const definitiveAuthFailure =
            refreshStatus === 400 || refreshStatus === 401 || refreshStatus === 403;
          if (!definitiveAuthFailure) {
            console.warn('[Auth] Visibility refresh failed transiently — keeping session', error);
            return;
          }
          // Session is truly expired and can't be refreshed — force re-login
          console.warn('[Auth] Session expired, redirecting to login');
          setUser(null);
          setUserType(null);
          setProviderAccess(null);
          setUserPlatformRoles({});
          setUserSystemRoles([]);
          setPlatformOverrides({});
          setFacilityAssignments([]);
          setFacilityUser(null);
          setFacilityInfo(null);
          setFacilityRole(null);
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(ROLES_STORAGE_KEY);
          localStorage.removeItem(USER_TYPE_KEY);
          localStorage.removeItem(PROVIDER_ACCESS_KEY);
          localStorage.removeItem(OVERRIDES_STORAGE_KEY);
          localStorage.removeItem(FACILITY_ASSIGNMENTS_KEY);
          localStorage.removeItem(FACILITY_USER_KEY);
          localStorage.removeItem(FACILITY_INFO_KEY);
          localStorage.removeItem(FACILITY_ROLE_KEY);
          localStorage.removeItem(SYSTEM_ROLES_STORAGE_KEY);
          // Native app: /login is outside the Capacitor /mobile scope and would
          // bounce the user to Safari — keep the redirect in-app.
          window.location.href = loginRedirectPath();
          return;
        }

        // Session refreshed successfully — no need to re-resolve user
        // unless we detect the user state is stale (null when it shouldn't be)
        if (!user && data.session.user) {
          // User state was cleared but session is valid — re-resolve
          const result = await resolveDbUser(data.session.user.id, data.session.user.email);
          if (result.user) {
            setUser(result.user);
            setUserType(result.userType);
            setProviderAccess(result.providerAccess);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(result.user));
            if (result.userType) localStorage.setItem(USER_TYPE_KEY, result.userType);
            if (result.providerAccess) localStorage.setItem(PROVIDER_ACCESS_KEY, JSON.stringify(result.providerAccess));
            if (result.userType === 'admin' || result.userType === 'provider') {
              await loadPlatformRoles(result.user);
              await loadOverrides(result.user);
              await loadCapabilities(result.user);
            }
          }
        }
      } catch (err) {
        console.warn('[Auth] Visibility refresh failed:', err);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, resolveDbUser, loadPlatformRoles, loadOverrides, marketingInert]);

  const login = async (email: string, password: string): Promise<AppUser> => {
    const data = await authService.login(email, password);
    const result = await resolveDbUser(data.user.id, data.user.email);
    if (!result.user) {
      await authService.logout();
      throw new Error('Account not found or inactive. Contact your administrator.');
    }
    setUser(result.user);
    setUserType(result.userType);
    setProviderAccess(result.providerAccess);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(result.user));
    if (result.userType) {
      localStorage.setItem(USER_TYPE_KEY, result.userType);
    }
    if (result.providerAccess) {
      localStorage.setItem(PROVIDER_ACCESS_KEY, JSON.stringify(result.providerAccess));
    }
    // Save facility assignments
    if (result.facilityAssignments) {
      setFacilityAssignments(result.facilityAssignments);
      localStorage.setItem(FACILITY_ASSIGNMENTS_KEY, JSON.stringify(result.facilityAssignments));
    }
    // Save facility user data
    if (result.facilityUser) {
      setFacilityUser(result.facilityUser);
      localStorage.setItem(FACILITY_USER_KEY, JSON.stringify(result.facilityUser));
    }
    if (result.facilityInfo) {
      setFacilityInfo(result.facilityInfo);
      localStorage.setItem(FACILITY_INFO_KEY, JSON.stringify(result.facilityInfo));
    }
    if (result.facilityRole) {
      setFacilityRole(result.facilityRole);
      localStorage.setItem(FACILITY_ROLE_KEY, JSON.stringify(result.facilityRole));
    }
    // Load platform roles and overrides for admin and provider users
    if (result.userType === 'admin' || result.userType === 'provider') {
      await loadPlatformRoles(result.user);
      await loadOverrides(result.user);
      await loadCapabilities(result.user);
    }
    return result.user;
  };

  const logout = async () => {
    // Always clear local state and storage, even if Supabase signOut fails
    try {
      await authService.logout();
    } catch {
      // signOut may fail due to network issues — clear local state regardless
    }
    setUser(null);
    setUserType(null);
    setProviderAccess(null);
    setUserPlatformRoles({});
    setPlatformOverrides({});
    setUserCapabilities([]);
    setFacilityAssignments([]);
    setFacilityUser(null);
    setFacilityInfo(null);
    setFacilityRole(null);
    setViewModeState(null);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(ROLES_STORAGE_KEY);
    localStorage.removeItem(USER_TYPE_KEY);
    localStorage.removeItem(PROVIDER_ACCESS_KEY);
    localStorage.removeItem(VIEW_MODE_KEY);
    localStorage.removeItem(OVERRIDES_STORAGE_KEY);
    localStorage.removeItem(FACILITY_ASSIGNMENTS_KEY);
    localStorage.removeItem(FACILITY_USER_KEY);
    localStorage.removeItem(FACILITY_INFO_KEY);
    localStorage.removeItem(FACILITY_ROLE_KEY);
    // Also purge any Supabase auth tokens from localStorage
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
        localStorage.removeItem(key);
      }
    });
    // Force hard redirect to login page (clears React state tree). Native app:
    // route in-scope so the Capacitor WebView doesn't bounce to Safari.
    window.location.href = loginRedirectPath();
  };

  // Determine linked provider ID from user record
  const linkedProviderId = user?.provider_id || null;

  // Profile switcher policy:
  //   Only `developer` and `super_admin` users get a view-mode toggle (used to
  //   preview the app as a provider / admin / facility user). Everyone else
  //   sees exactly what their actual permissions grant — their own assigned
  //   platforms and sections — so they don't need (and shouldn't see) a
  //   toggle. ProfileSwitcher hides itself when availableViewModes.length <= 1.
  //
  // RBAC redesign 2026-05-11: provider-linked users who ALSO hold an admin-
  // tier platform role get BOTH modes (provider + admin) so they can flip
  // between their provider self-routes and the admin tools they're explicitly
  // granted. Previously they were locked to 'provider', which hid Schedule
  // Builder (etc.) from chairs and other provider-linked admins even though
  // their DB perms allowed it. See docs/rbac-redesign-spec.md.
  const hasPlatformRoleAssignments = Object.keys(userPlatformRoles).length > 0 || Object.keys(platformOverrides).length > 0;
  // Has any platform-admin-tier role (in base assignments or overrides)?
  // RBAC Slice D (2026-05-12): backfill migration ensured every role with
  // admin-tier permissions has `is_platform_admin = true`. Trust the flag
  // directly — no more inference from permissions shape.
  const isRoleAdminTier = (r: PlatformRoleInfo): boolean => r.isPlatformAdmin === true;
  const hasAnyAdminPlatformRole =
    Object.values(userPlatformRoles).some(isRoleAdminTier) ||
    Object.values(platformOverrides).some(isRoleAdminTier);
  // RBAC redesign 2026-05-12: the profile/view-mode picker is exclusively
  // a developer + super_admin tool for previewing other personas. Every
  // other user gets exactly ONE view mode that matches their actual
  // permissions — no picker, no switching.
  //
  // Resolution rules:
  //   - Developer  → 4 modes (developer/super_admin/admin/provider) — picker visible
  //   - Super Admin → 3 modes (super_admin/admin/provider) — picker visible
  //   - Facility user → ['facility'] — picker hidden
  //   - Provider-linked WITH admin platform role OR is_admin flag → ['admin']
  //       Their admin sidebar surfaces every platform they're granted; the
  //       provider self-routes (/provider/*, /scheduling/my-shifts, etc.)
  //       remain reachable by URL and via the "My Portal" entry at the top
  //       of the admin sidebar. No need to switch to a provider view.
  //   - Provider-linked, no admin role → ['provider'] — picker hidden
  //   - Staff admin (no provider link) → ['admin'] — picker hidden
  // RBAC Round 4 PR-C (2026-05-21): legacy users.role fallbacks dropped.
  // Every god-mode user now holds the corresponding system role; the
  // legacy column is stripped in PR-D.
  const isDeveloperHere = userSystemRoles.includes('Developer');
  const isSuperAdminHere = userSystemRoles.includes('Super Admin') || isDeveloperHere;
  const availableViewModes: ViewMode[] = [];
  if (isDeveloperHere) {
    availableViewModes.push('developer', 'super_admin', 'admin', 'provider');
  } else if (isSuperAdminHere) {
    availableViewModes.push('super_admin', 'admin', 'provider');
  } else if (userType === 'facility') {
    availableViewModes.push('facility');
  } else if (userType === 'provider') {
    // Provider-linked users see ONE view. If they hold any admin-tier
    // platform role (chair, chief, credentialer, etc.) OR the legacy
    // is_admin flag, that one view is 'admin' — their admin sidebar
    // includes My Portal for provider self-routes. Otherwise they get
    // the pure provider menu.
    if (hasAnyAdminPlatformRole || user?.is_admin === true) {
      availableViewModes.push('admin');
    } else {
      availableViewModes.push('provider');
    }
  } else {
    // Staff admins (is_admin, role='admin', or platform-role-based access).
    availableViewModes.push('admin');
  }

  // Load the Provider system role's platform roles for preview mode
  const loadProviderSystemRoles = useCallback(async () => {
    if (providerSystemRoles !== null) return providerSystemRoles; // Already loaded
    if (!user?.auth_user_id) return null;
    try {
      const res = await fetch(`/api/admin/system-roles/preview?roleName=Provider&callerAuthId=${user.auth_user_id}`);
      if (!res.ok) return null;
      const { platformRoles } = await res.json();
      setProviderSystemRoles(platformRoles);
      return platformRoles;
    } catch {
      return null;
    }
  }, [providerSystemRoles, user?.auth_user_id]);

  // Apply roles for a given view mode
  // RBAC Round 4 PR-C: legacy users.role checks replaced with system-role
  // membership lookups. "Privileged" here means Developer or Super Admin
  // (the two roles that can flip into provider preview mode).
  const applyViewModeRoles = useCallback(async (mode: ViewMode) => {
    const isPrivileged =
      userSystemRoles.includes('Developer') || userSystemRoles.includes('Super Admin');
    if (mode === 'provider' && isPrivileged) {
      // Save the user's own roles if we haven't already
      if (Object.keys(ownPlatformRoles).length === 0 && Object.keys(userPlatformRoles).length > 0) {
        setOwnPlatformRoles(userPlatformRoles);
      }
      // Load and apply the Provider system role's platform roles
      const provRoles = await loadProviderSystemRoles();
      if (provRoles) {
        setUserPlatformRoles(provRoles);
      }
    } else if (isPrivileged) {
      // Switching back to developer/super_admin/admin — restore empty roles (full access)
      setUserPlatformRoles({});
    } else if (Object.keys(ownPlatformRoles).length > 0) {
      // Restore original roles for non-super-admin
      setUserPlatformRoles(ownPlatformRoles);
    }
  }, [userSystemRoles, ownPlatformRoles, userPlatformRoles, loadProviderSystemRoles]);

  // Set view mode with persistence and role swapping
  const setViewMode = useCallback(async (mode: ViewMode) => {
    if (availableViewModes.includes(mode)) {
      setViewModeState(mode);
      localStorage.setItem(VIEW_MODE_KEY, mode);
      await applyViewModeRoles(mode);
    }
  }, [availableViewModes, applyViewModeRoles]);

  // Initialize view mode from storage or default to highest privilege
  useEffect(() => {
    if (user && availableViewModes.length > 0) {
      const storedMode = localStorage.getItem(VIEW_MODE_KEY) as ViewMode | null;
      if (storedMode && availableViewModes.includes(storedMode)) {
        setViewModeState(storedMode);
        // Apply roles for restored view mode
        if (
          storedMode === 'provider' &&
          (userSystemRoles.includes('Developer') || userSystemRoles.includes('Super Admin'))
        ) {
          applyViewModeRoles(storedMode);
        }
      } else {
        // Default to highest privilege mode
        setViewModeState(availableViewModes[0]);
      }
    }
  }, [user, availableViewModes.length]);

  // Effective permissions based on view mode.
  //
  // RBAC Slice B (2026-05-11): "is developer" / "is super_admin" are now
  // derived primarily from the user's assigned system roles (Developer /
  // Super Admin entries in `user_system_roles`). The legacy `users.role`
  // column is kept as a fallback while we migrate the rest of the code
  // off it — once Slice D ships and the column is removed, the fallback
  // goes away too.
  const hasDeveloperSystemRole = userSystemRoles.includes('Developer');
  const hasSuperAdminSystemRole = userSystemRoles.includes('Super Admin') || hasDeveloperSystemRole;
  // RBAC Round 4 PR-C: legacy users.role fallbacks removed; system roles are
  // the single source of truth.
  const isDeveloperByAnySource = hasDeveloperSystemRole;
  const isSuperAdminByAnySource = hasSuperAdminSystemRole;

  const effectiveIsDeveloper = (viewMode === 'developer' || viewMode === 'super_admin') && isDeveloperByAnySource;
  const effectiveIsSuperAdmin = (viewMode === 'developer' || viewMode === 'super_admin') && isSuperAdminByAnySource;
  // isAdmin = developer, super_admin, explicit is_admin flag, OR holds an
  // admin-tier platform role assignment. The platform-role path was added
  // in the RBAC redesign (2026-05-11) so provider-linked users with admin
  // platform roles count as admins when they flip into admin view mode.
  // RBAC Round 4 PR-C: legacy user.is_admin removed; hasAnyAdminPlatformRole
  // (any user_platform_roles row with is_platform_admin=true) is the
  // canonical platform-admin signal.
  const effectiveIsAdmin = viewMode !== 'provider' && viewMode !== 'facility' && (
    isDeveloperByAnySource ||
    isSuperAdminByAnySource ||
    hasAnyAdminPlatformRole
  );
  const effectiveIsProvider = viewMode === 'provider';
  const effectiveIsFacilityUser = viewMode === 'facility' || userType === 'facility';

  const isDeveloper = effectiveIsDeveloper;
  const isSuperAdmin = effectiveIsSuperAdmin;
  const isAdmin = effectiveIsAdmin;
  // RBAC Round 4 PR-C: "read-only" now means staff with system role
  // assignment(s) who aren't admin/provider/facility. Replaces the legacy
  // users.role === 'read_only' check. Captures the Billing Specialist case
  // (system role with viewer-only platform grants) without depending on
  // the legacy column.
  const isReadOnly = userSystemRoles.length > 0
    && !isDeveloperByAnySource
    && !isSuperAdminByAnySource
    && !hasAnyAdminPlatformRole
    && userType !== 'provider'
    && userType !== 'facility';
  const isProvider = effectiveIsProvider;
  const isEquityPartner = user?.is_equity_partner === true;
  const isFacilityUser = effectiveIsFacilityUser;
  const canEdit = (!isReadOnly || hasPlatformRoleAssignments) && !isProvider && user !== null;

  // Platform access logic — single source of truth: user_platform_roles
  // ALL users (including super_admin/developer) get sidebar access from user_platform_roles.
  // super_admin is a system role that grants elevated privileges (edit access, admin UI, etc.)
  // but does NOT auto-grant platform visibility. Platforms can be selectively removed.
  // Facility users are the exception — their access comes from facility module mapping.
  const overridePlatforms = Object.keys(platformOverrides);
  const facilityPlatforms = isFacilityUser && facilityUser?.modules
    ? [...new Set(facilityUser.modules.flatMap(m => FACILITY_MODULE_PLATFORMS[m] || []))]
    : [];

  const rawAssignedPlatforms = [...new Set([...Object.keys(userPlatformRoles), ...overridePlatforms])];

  // Defense-in-depth: if a developer or super_admin has no platform roles loaded
  // (e.g. the /api/admin/user-roles call failed, localStorage was flushed before
  // the network call completed, or DB assignments were wiped), fall back to full
  // platform access rather than collapsing the sidebar and locking them out of
  // the admin UI they need to fix it. Super_admins still lose the dev-only
  // platforms per DEVELOPER_ONLY_PLATFORMS; developers get everything.
  // RBAC Round 4 PR-C: legacy users.role replaced with system_roles lookup.
  const isPrivilegedSystemUser = isDeveloperByAnySource || isSuperAdminByAnySource;
  const platformRolesMissing = Object.keys(userPlatformRoles).length === 0 && overridePlatforms.length === 0;
  const useFallback = isPrivilegedSystemUser && platformRolesMissing && viewMode !== 'provider' && viewMode !== 'facility';
  const fallbackPlatforms = isDeveloperByAnySource
    ? [...ALL_PLATFORMS, ...DEVELOPER_ONLY_PLATFORMS, 'rates']
    : [...ALL_PLATFORMS, 'rates'];
  const assignedPlatforms = useFallback ? [...new Set(fallbackPlatforms)] : rawAssignedPlatforms;

  const CONDITIONAL_PLATFORMS: string[] = [];

  // Policies & tutorials are shared resources every provider should see
  // regardless of whether they have an explicit user_platform_roles row for
  // them. Admins still control WHAT a provider sees (policies are scoped by
  // the provider's facility assignments; tutorials are scoped by the
  // provider's category via tutorial_modules.applies_to_tracks). These two
  // platforms are auto-added to provider accessiblePlatforms.
  const PROVIDER_AUTO_PLATFORMS = ['policies', 'tutorials'];

  // Advanced Intelligence — auto-granted to dev/super_admin only. Not
  // exposed via user_platform_roles. Hard-coded so we can't accidentally
  // grant it to a non-privileged account through the admin UI.
  const adminAutoPlatforms = (effectiveIsSuperAdmin || effectiveIsDeveloper)
    ? INTELLIGENCE_AUTO_PLATFORMS
    : [];

  const accessiblePlatforms = isFacilityUser
    ? facilityPlatforms
    : isProvider
      ? [...new Set([
          ...assignedPlatforms.filter(p => PROVIDER_PLATFORMS.includes(p)),
          ...PROVIDER_AUTO_PLATFORMS,
          ...CONDITIONAL_PLATFORMS,
        ])]
      : [...new Set([...assignedPlatforms, ...CONDITIONAL_PLATFORMS, ...adminAutoPlatforms])];

  const hasAccess = (platform: string) => accessiblePlatforms.includes(platform);

  // Check if user has access to a specific section within a platform
  const hasSectionAccess = (platform: string, sectionKey: string): boolean => {
    // Super admins always have full access
    if (isSuperAdmin) return true;
    // Providers have view-level access to auto-granted shared-resource platforms
    // (policies, tutorials) regardless of whether they have a user_platform_roles
    // row for them. Matches the accessiblePlatforms auto-add above.
    if (isProvider && PROVIDER_AUTO_PLATFORMS.includes(platform)) {
      return true;
    }
    // Providers without role data: only allow access to platforms they have explicit access to
    if (isProvider && Object.keys(userPlatformRoles).length === 0) {
      return accessiblePlatforms.includes(platform);
    }
    // Admin users without role assignments: allow section access to platforms they can see
    if (Object.keys(userPlatformRoles).length === 0) {
      return accessiblePlatforms.includes(platform);
    }
    // Check the user's role permissions for this platform
    const roleInfo = userPlatformRoles[platform];
    if (!roleInfo) return false;
    // If permissions object is empty, allow all (full access role)
    if (Object.keys(roleInfo.permissions).length === 0) return true;
    const perm = roleInfo.permissions[sectionKey];
    // Support both boolean (legacy) and tier-based string permissions.
    // 'admin' string is legacy (Slice E dropped admin as a section tier),
    // accept it at runtime for backwards compat with un-migrated DB rows.
    return perm === true || perm === 'view' || perm === 'edit' || perm === 'admin';
  };

  // Get the effective access level for a section (view/edit/none).
  // Slice E: 'admin' is no longer a section-level tier; legacy DB 'admin'
  // strings get normalized to 'edit' via permissions.ts:normalizePermissionValue.
  const getAccessLevelFn = (platform: string, sectionKey: string): AccessLevel => {
    if (isSuperAdmin) return 'edit';
    // Check override first (takes priority)
    const override = platformOverrides[platform];
    if (override) {
      return getEffectiveLevel(override.permissions, sectionKey);
    }
    // Check base role
    const roleInfo = userPlatformRoles[platform];
    if (!roleInfo) return 'none';
    return getEffectiveLevel(roleInfo.permissions, sectionKey);
  };

  // Enhanced permission methods (Phase 2)
  // These check overrides first (they take priority), then fall back to base roles
  const canView = (platform: string, sectionKey: string): boolean => {
    if (isSuperAdmin) return true;
    // Check override first (takes priority)
    const override = platformOverrides[platform];
    if (override) {
      return canViewSection(override.permissions, sectionKey);
    }
    // Fall back to regular section access
    return hasSectionAccess(platform, sectionKey);
  };

  const canEditSectionFn = (platform: string, sectionKey: string): boolean => {
    if (isSuperAdmin) return true;
    // Check override first (takes priority)
    const override = platformOverrides[platform];
    if (override) {
      return canEditSection(override.permissions, sectionKey);
    }
    // Check base role with tier-based permissions
    const roleInfo = userPlatformRoles[platform];
    if (roleInfo) {
      return canEditSection(roleInfo.permissions, sectionKey);
    }
    // Providers without explicit role data default to view-only
    if (isProvider) return false;
    // Admin/staff without role data: use legacy section access check
    return hasSectionAccess(platform, sectionKey);
  };

  const isAdminForPlatformFn = (platform: string): boolean => {
    if (isSuperAdmin) return true;
    // RBAC Slice D (2026-05-12): trust `isPlatformAdmin` flag directly.
    // Backfill migration ensured every role with admin-tier permissions
    // has the flag set; no inference from permissions shape needed.
    const override = platformOverrides[platform];
    if (override) return override.isPlatformAdmin === true;
    const role = userPlatformRoles[platform];
    if (role) return role.isPlatformAdmin === true;
    return false;
  };

  const getUserPlatforms = (): string[] => accessiblePlatforms;

  // Which recruiting provider-type modules does this user actually have?
  // Super admins / developers with no role rows loaded fall back to both
  // modules (matches the fallbackPlatforms behaviour for the legacy
  // `recruiting` entry above).
  const recruitingProviderModules: Array<'MD' | 'CRNA'> = (() => {
    if (useFallback) return ['MD', 'CRNA'];
    const modules: Array<'MD' | 'CRNA'> = [];
    for (const mod of RECRUITING_MODULES) {
      if (userPlatformRoles[mod] || platformOverrides[mod]) {
        modules.push(RECRUITING_MODULE_TO_PROVIDER_TYPE[mod]);
      }
    }
    return modules;
  })();

  // Developers always implicitly hold every capability. Super admins and
  // other users hold only what their assigned system roles grant via
  // system_role_capabilities (loaded above).
  const hasCapability = useCallback((capability: string): boolean => {
    // RBAC Round 4 PR-C: legacy users.role replaced with Developer system role.
    if (hasDeveloperSystemRole) return true;
    return userCapabilities.includes(capability);
  }, [hasDeveloperSystemRole, userCapabilities]);

  // Facility assignment computed values
  // Super admins always have all-facility access
  const hasAllFacilityAccess = isSuperAdmin || facilityAssignments.some(a => a.facility_id === null);
  const assignedFacilityIds = hasAllFacilityAccess
    ? [] // empty means "all" — Sidebar will show all facilities from FacilityContext
    : facilityAssignments
        .filter(a => a.facility_id !== null)
        .map(a => a.facility_id as string);

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout,
      isDeveloper, isSuperAdmin, isAdmin, isReadOnly, isProvider, isEquityPartner, canEdit, userSystemRoles,
      accessiblePlatforms, hasAccess,
      userPlatformRoles, hasSectionAccess, getAccessLevel: getAccessLevelFn, getUserPlatforms,
      refreshPlatformRoles,
      providerAccess, userType,
      // Facility user
      isFacilityUser, facilityUser, facilityInfo, facilityRole,
      // Profile switching
      viewMode,
      setViewMode,
      availableViewModes,
      linkedProviderId,
      // Phase 2: Enhanced permission methods
      canView,
      canEditSection: canEditSectionFn,
      isAdminForPlatform: isAdminForPlatformFn,
      platformOverrides,
      loadOverrides: () => loadOverrides(user),
      // Facility assignments
      facilityAssignments,
      hasAllFacilityAccess,
      assignedFacilityIds,
      // System-role capabilities
      userCapabilities,
      hasCapability,
      // Recruiting provider-type module scope
      recruitingProviderModules,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

// Export the section key mapping and dual-tab modules for use in Sidebar and components
export { MENU_LABEL_TO_SECTION_KEY };
